/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { useContext } from 'react';
import LocationSelectorFilter from '../LocationSelector';
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Button from '../../elements/Button';
import { MobileFooter } from '../../lab/MobileFilterView';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';

const TravelClassFilter = React.forwardRef((props, receivedRef) => {
  const ownRef = useRef();
  const ref = receivedRef || ownRef;
  const { translate } = React.useContext(Context);
  const { options, isDirty, setWasClear } = props;
  const locationSelectorFilterRef = useRef();
  const travelClassOptions = React.useMemo(() => {
    return (Array.isArray(options) ? options : []).map((option) => ({
      ...option,
      label: translate(`cmp-travel-class--${option.label}`)
    }));
  }, [options, translate]);
  const {
    enabledFooter = false,
    footerContainer = {},
    buttonsContainer = {},
    doneButtonSettings = {}
  } = props;

  const locationSelectorRef = React.useRef(null);
  const contentData = useContext(Context);

  const item = travelClassOptions.find((i) => {
    return i['value'] === props.defaultValue;
  });

  const [selectedValue, setSelectedValue] = React.useState(item);

  const handleSave = () => {
    const select = locationSelectorRef.current.getSelectedItem();
    setSelectedValue(select);
    if (locationSelectorRef) {
      locationSelectorRef.current?.onCloseMenu();
    }
    props.onChange(select);
    props.goBack();
  };

  const renderFooter = () => (
    <Box display="flex" {...footerContainer}>
      <Row {...buttonsContainer}>
        <Col>
          <Button {...doneButtonSettings} disabled={false} onClick={handleSave}>
            {translate('cmp-travel-class-done-button', 'Done')}
          </Button>
        </Col>
      </Row>
    </Box>
  );

  useEffect(() => {
    if (enabledFooter) {
      if (locationSelectorRef.current && selectedValue) {
        locationSelectorRef.current.updateState(selectedValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isFullMode]);

  return enabledFooter ? (
    <EmotionCache direction={contentData.direction}>
      <ThemeProvider theme={contentData.theme.designTokens}>
        <LocationSelectorFilter
          {...props}
          ref={locationSelectorRef}
          options={travelClassOptions}
          dataTestId="locationSelector-travelClass"
          placeholder={translate('cmp-location-travel-class-placeholder', 'Select Travel Class')}
          inputFocusReferences={
            enabledFooter ? locationSelectorFilterRef : ref ?? locationSelectorFilterRef
          }
          onBlur={() => {
            if (isDirty) {
              setWasClear(false);
            }
          }}
          allowVariableHighlightedIndex={true}
          execOnChangeV2={true}
        />
        {enabledFooter && props.isMobile && props.isFullMode && (
          <MobileFooter align="end" sx={{ width: '100%', right: 0 }}>
            {renderFooter()}
          </MobileFooter>
        )}
      </ThemeProvider>
    </EmotionCache>
  ) : (
    <LocationSelectorFilter
      {...props}
      options={travelClassOptions}
      dataTestId="locationSelector-travelClass"
      placeholder={translate('cmp-location-travel-class-placeholder', 'Select Travel Class')}
      inputFocusReferences={ref ?? locationSelectorFilterRef}
      allowVariableHighlightedIndex={true}
      onBlur={() => {
        if (isDirty) {
          setWasClear(false);
        }
      }}
    />
  );
});

TravelClassFilter.defaultProps = {
  icon: 'flight-type',
  options: [
    { label: 'economy', value: 'ECONOMY' },
    { label: 'business', value: 'BUSINESS' },
    { label: 'first', value: 'FIRST' }
  ],
  labelKey: 'travel-class',
  showSelectorMenuArrow: false,
  openOnFocus: true,
  showLabelInside: false,
  clearable: false,
  selectMode: true,
  showCode: false,
  showArrowsIfClearable: false,
  menuItem: {
    shadow: '4',
    paddingHeight: '0',
    rounded: '0',
    border: true
  },
  inputItem: {
    shadow: '@theme:input.shadow',
    rounded: '@theme:input.rounded'
  },
  listItem: {
    hoverBgColor: 'primary',
    hoverTextColor: 'white',
    paddingLeft: '4',
    paddingRight: '1',
    paddingHeight: '2'
  },
  groupItem: {
    hoverBgColor: 'primary',
    hoverTextColor: 'white',
    paddingWidth: '1',
    paddingHeight: '2'
  },
  noResultItem: {
    paddingWidth: '1',
    paddingHeight: '2'
  }
};

export default TravelClassFilter;
